import React, { useEffect, useState, useRef, useCallback } from 'react';
import { MapPin, Loader2 } from 'lucide-react';
import { loadGoogleMaps } from '../lib/googlePlaces';

/**
 * AddressAutocomplete — drop-in replacement for the Pickup/Delivery address
 * textarea. Renders Google Places suggestions in a dropdown below the field
 * while the customer types. On selection, it autofills:
 *   • street (textarea value via onChange)
 *   • area  (via onAutofill)
 *   • pincode (via onAutofill)
 *   • city  (via onAutofill)  — only forwarded; the calling form decides
 *     whether to overwrite its existing city dropdown.
 *
 * The component is a controlled wrapper around the existing textarea so it
 * keeps the form's overall validation/state shape unchanged.
 */

const COUNTRY_RESTRICTION = { country: 'in' };
const DEBOUNCE_MS = 280;
const MIN_CHARS = 3;

const parseAddressComponents = (components = []) => {
  // Map Google's address_components into our pickup/delivery field shape.
  const get = (type) => components.find((c) => c.types.includes(type));
  const street = [
    get('subpremise')?.long_name,
    get('premise')?.long_name,
    get('street_number')?.long_name,
    get('route')?.long_name,
  ].filter(Boolean).join(' ').trim();
  const area = [
    get('sublocality_level_2')?.long_name,
    get('sublocality_level_1')?.long_name,
    get('sublocality')?.long_name,
    get('neighborhood')?.long_name,
  ].filter(Boolean).join(', ');
  const city = get('locality')?.long_name
    || get('postal_town')?.long_name
    || get('administrative_area_level_2')?.long_name
    || '';
  const pincode = get('postal_code')?.long_name || '';
  return { street, area, city, pincode };
};

export const AddressAutocomplete = ({
  value,
  onChange,        // (evt) => syncs the textarea value back into form state
  onAutofill,      // ({street, area, city, pincode, formatted_address}) => splits a Google pick into the four fields
  cityHint,        // e.g. "Bangalore" — biases predictions and is appended to query
  name = 'address',
  placeholder = 'House / Building / Street',
  rows = 2,
  testIdInput,
  testIdSuggestions = `${testIdInput || 'address'}-suggestions`,
  className = 'w-full',
  disabled = false,
}) => {
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState('');
  const [highlightedIdx, setHighlightedIdx] = useState(-1);

  const acService = useRef(null);   // AutocompleteService
  const detailsSvc = useRef(null);  // PlacesService (needs a DOM node)
  const sessionToken = useRef(null);
  const detailsHost = useRef(null);
  const debounceTimer = useRef(null);
  const containerRef = useRef(null);

  // Boot Google APIs once
  useEffect(() => {
    let cancelled = false;
    loadGoogleMaps().then((google) => {
      if (cancelled) return;
      acService.current = new google.maps.places.AutocompleteService();
      sessionToken.current = new google.maps.places.AutocompleteSessionToken();
      // PlacesService requires a DOM element (or a Map). A hidden div is fine.
      detailsSvc.current = new google.maps.places.PlacesService(detailsHost.current);
    }).catch((e) => {
      setError(e.message || 'Could not load Google Places');
    });
    return () => { cancelled = true; };
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (!containerRef.current || containerRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const fetchPredictions = useCallback((input) => {
    if (!acService.current) return;
    const trimmed = (input || '').trim();
    if (trimmed.length < MIN_CHARS) {
      setPredictions([]);
      setOpen(false);
      return;
    }
    setLoading(true);
    const biasedInput = cityHint && !trimmed.toLowerCase().includes(cityHint.toLowerCase())
      ? `${trimmed}, ${cityHint}`
      : trimmed;
    acService.current.getPlacePredictions(
      {
        input: biasedInput,
        componentRestrictions: COUNTRY_RESTRICTION,
        sessionToken: sessionToken.current,
        // Bias towards street addresses, establishments and POIs
        types: ['geocode', 'establishment'],
      },
      (results, status) => {
        setLoading(false);
        if (status !== 'OK' || !results) {
          setPredictions([]);
          // Status `ZERO_RESULTS` is normal — don't surface as an error
          if (status !== 'ZERO_RESULTS') {
            console.warn('Places autocomplete status:', status);
          }
          setOpen(true);  // still open with "no matches" state
          return;
        }
        setPredictions(results);
        setOpen(true);
        setHighlightedIdx(-1);
      }
    );
  }, [cityHint]);

  const handleInputChange = (e) => {
    onChange(e);
    const v = e.target.value;
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => fetchPredictions(v), DEBOUNCE_MS);
  };

  const pickPrediction = (pred) => {
    if (!detailsSvc.current || !pred) return;
    detailsSvc.current.getDetails(
      {
        placeId: pred.place_id,
        fields: ['address_components', 'formatted_address', 'geometry', 'name'],
        sessionToken: sessionToken.current,
      },
      (place, status) => {
        if (status !== 'OK' || !place) return;
        // Rotate the session token so the next typing session is billed as a new one
        if (window.google?.maps?.places) {
          sessionToken.current = new window.google.maps.places.AutocompleteSessionToken();
        }
        const parsed = parseAddressComponents(place.address_components || []);
        const formatted = place.formatted_address || pred.description;
        // The "street" portion + name covers door / building / street.
        const streetText = [place.name, parsed.street].filter((s, i, arr) =>
          s && arr.indexOf(s) === i && s !== parsed.area && s !== parsed.city
        ).join(', ');
        const finalStreet = streetText || parsed.street || formatted;

        // Sync the textarea with the parsed street value
        onChange({ target: { name, value: finalStreet } });
        if (onAutofill) {
          onAutofill({
            street: finalStreet,
            area: parsed.area,
            city: parsed.city,
            pincode: parsed.pincode,
            formatted_address: formatted,
          });
        }
        setOpen(false);
        setPredictions([]);
      }
    );
  };

  const handleKeyDown = (e) => {
    if (!open || predictions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIdx((i) => Math.min(i + 1, predictions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && highlightedIdx >= 0) {
      e.preventDefault();
      pickPrediction(predictions[highlightedIdx]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div ref={containerRef} className="relative" data-testid={`${testIdInput || 'address'}-wrap`}>
      <textarea
        data-testid={testIdInput}
        name={name}
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => { if (predictions.length) setOpen(true); }}
        placeholder={placeholder}
        rows={rows}
        autoComplete="off"
        disabled={disabled}
        className={className}
      />

      {/* Hidden host for the PlacesService (Google requires a DOM target) */}
      <div ref={detailsHost} style={{ display: 'none' }} aria-hidden="true" />

      {open && (
        <div
          data-testid={testIdSuggestions}
          className="absolute z-30 left-0 right-0 mt-1 rounded-xl bg-white border border-[#1A1A1A]/15 shadow-[0_12px_32px_rgba(0,0,0,0.12)] max-h-72 overflow-y-auto"
        >
          {loading && (
            <div className="px-4 py-3 flex items-center gap-2 text-sm text-[#525252]">
              <Loader2 size={14} className="animate-spin" /> Searching addresses…
            </div>
          )}

          {!loading && predictions.length === 0 && !error && (
            <div className="px-4 py-3 text-xs text-[#737373]">
              No matches yet — keep typing your street or landmark.
            </div>
          )}

          {!loading && error && (
            <div className="px-4 py-3 text-xs text-[#B91C1C]">
              {error}
            </div>
          )}

          {!loading && predictions.map((p, idx) => {
            const main = p.structured_formatting?.main_text || p.description;
            const sub = p.structured_formatting?.secondary_text || '';
            const isActive = idx === highlightedIdx;
            return (
              <button
                key={p.place_id}
                type="button"
                onMouseEnter={() => setHighlightedIdx(idx)}
                onMouseDown={(e) => { e.preventDefault(); pickPrediction(p); }}
                data-testid={`${testIdInput || 'address'}-prediction-${idx}`}
                className={`w-full text-left flex items-start gap-2 px-4 py-2.5 border-b border-[#1A1A1A]/5 last:border-b-0 transition-colors ${
                  isActive ? 'bg-[#FFF1E8]' : 'hover:bg-[#FAF6EE]'
                }`}
              >
                <MapPin size={14} className="mt-1 flex-shrink-0 text-[#FF5A1F]" />
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-medium text-[#1A1A1A] truncate">{main}</span>
                  {sub && <span className="block text-xs text-[#737373] truncate">{sub}</span>}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete;
